import { connect } from 'react-redux'
import { string, bool, oneOfType } from 'prop-types'

import {
  hasInstallationAppTutorial,
  isAppInstallable,
  isAppInstalled,
  getPublishedApp,
  isAuthorized,
  isUserInitialized,
  isPrivateApp,
  checkCustomPermissions,
  isPublishedAppsFetching,
  isAvailableInCurrentPlan,
  isProductLicenseActive,
  getProductDesignation,
  checkIfUserNotAssignedToActiveProduct,
  isAppInReview,
  isSubscriptionFetching,
  isResellerLicense,
} from '@/store/selectors'
import { useOnboarding } from '@/context/OnboardingContext'
import { OneClickPaymentContextProvider } from '@/context/OneClickPayment'
import useHydrationContext from '@/hooks/useHydrationContext'

import InstallableButton from './Installable'
import BuyableButton from './Buyable'
import NotInstallableButton from './NotInstallable'
import OpenTutorialButton from './OpenTutorial'
import InstalledButton from './Installed'
import Loader from './Loader'
import DefaultCase from './NotInstallable/DefaultCase'

const InstallButtonContainer = ({
  id,
  hasInstallationTutorial,
  isInstallable,
  isInstalled,
  isReadyToDisplay,
  isPrivateAndPaidApp,
  userHasNotEnoughPermissions,
  availableOnlyInHigherPlan,
  isLicenseInactive,
  userIsNotAssignedToActiveProduct,
  isBuyable,
  isReseller,
  ...props
}) => {
  const { isLoadingOnboarding } = useOnboarding()
  const hydrated = useHydrationContext()

  switch (true) {
    case !hydrated:
    case isLoadingOnboarding:
    case !isReadyToDisplay:
      return <Loader {...props} />

    case isInstalled:
      return <InstalledButton id={id} {...props} />

    case isPrivateAndPaidApp:
    case availableOnlyInHigherPlan:
    case userHasNotEnoughPermissions:
    case userIsNotAssignedToActiveProduct:
    case isLicenseInactive:
    case isReseller:
      return <NotInstallableButton id={id} {...props} />

    case hasInstallationTutorial:
      return <OpenTutorialButton id={id} {...props} />

    case isBuyable:
      return <BuyableButton id={id} {...props} />

    case isInstallable:
      return <InstallableButton id={id} {...props} />

    default:
      return <DefaultCase id={id} {...props} />
  }
}

const mapStateToProps = (state, ownProps) => {
  const { appId: id } = ownProps

  const app = getPublishedApp(state, id)
  const isInstallable = isAppInstallable(state, id)
  const isInstalled = isAppInstalled(state, id)
  const hasInstallationTutorial = hasInstallationAppTutorial(state, id)
  const isUserAuthorized = isAuthorized(state)
  const isUserFetched = isUserInitialized(state)
  const isPrivateAndPaidApp = isPrivateApp(state, id) && !!app.payment
  const availableInCurrentPlan = isAvailableInCurrentPlan(state, id)
  const availableOnlyInHigherPlan = !availableInCurrentPlan && isUserAuthorized
  const designation = getProductDesignation(state, id)
  const isLicenseInactive =
    !isProductLicenseActive(state, designation) && isUserAuthorized
  const isBuyable = !!app.payment && !isAppInReview(state, id)
  const isInstalledAppsFetched = state.installedApps.hasInitialized
  const isPublishedAppsFetched = !isPublishedAppsFetching(state)
  const isCartFetched = state.cart.isInitialized
  const isSubscriptionLoading = isSubscriptionFetching(state)
  const isReadyToDisplay =
    app &&
    isUserFetched &&
    (isUserAuthorized
      ? isInstalledAppsFetched && isCartFetched && !isSubscriptionLoading
      : isPublishedAppsFetched)
  const isReseller = isResellerLicense(state) && isBuyable

  const userHasNotEnoughPermissions =
    isUserAuthorized && !checkCustomPermissions(state, id)
  const userIsNotAssignedToActiveProduct =
    isUserAuthorized &&
    checkIfUserNotAssignedToActiveProduct(state, designation)

  return {
    id,
    isInstalled,
    isInstallable,
    hasInstallationTutorial,
    isReadyToDisplay,
    isPrivateAndPaidApp,
    userHasNotEnoughPermissions,
    availableOnlyInHigherPlan,
    isLicenseInactive,
    userIsNotAssignedToActiveProduct,
    isBuyable,
    isReseller,
  }
}

InstallButtonContainer.propTypes = {
  id: string,
  hasInstallationTutorial: oneOfType([string, bool]),
  isInstallable: bool,
  isInstalled: bool,
  isReadyToDisplay: bool,
  isPrivateAndPaidApp: bool,
  userHasNotEnoughPermissions: bool,
  availableOnlyInHigherPlan: bool,
  isLicenseInactive: bool,
  userIsNotAssignedToActiveProduct: bool,
  isBuyable: bool,
  isReseller: bool,
}

export default connect(mapStateToProps)((props) => (
  <OneClickPaymentContextProvider id={props.id}>
    <InstallButtonContainer {...props} />
  </OneClickPaymentContextProvider>
))
